import {
  Button,
  Chip,
  createStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";

import {
  fetchFreezeEvents,
  fetchFreezeEventsNext,
  getFreezeEvents,
  getFreezeEventsHasFetched,
  getFreezeEventsHasMore
} from "../../../../../modules/factoringClient";
import colors from "../../../../../theme/colors";

interface FreezeEvent {
  id: string;
  event_type:
    | "FROZE"
    | "UNFROZE"
    | "BLOCKED"
    | "FORCE_OVERRIDE"
    | "EXEMPT_TYPE_ADDED"
    | "EXEMPT_TYPE_REMOVED";
  actor: { id: string; email: string } | null;
  triggered_by: string;
  operation: string;
  related_object_type: string;
  related_object_id: string;
  reason: string;
  metadata: Record<string, any>;
  created: string;
}

const EVENT_COLORS: Record<string, string> = {
  FROZE: colors.orange,
  UNFROZE: colors.green,
  BLOCKED: colors.danger,
  FORCE_OVERRIDE: colors.purple,
  EXEMPT_TYPE_ADDED: colors.link,
  EXEMPT_TYPE_REMOVED: colors.grayText
};

const styles = () =>
  createStyles({
    container: {
      marginTop: 16
    },
    headerCell: {
      fontWeight: 600,
      color: colors.grayText,
      textTransform: "uppercase",
      fontSize: 12,
      letterSpacing: 0.5
    },
    timestamp: {
      whiteSpace: "nowrap",
      color: colors.textColor
    },
    operationCell: {
      whiteSpace: "nowrap"
    },
    chip: {
      color: colors.white,
      fontWeight: 600,
      letterSpacing: 0.5,
      marginRight: 8
    },
    operationText: {
      color: colors.grayText,
      marginRight: 8
    },
    frLink: {
      color: colors.link
    },
    user: {
      fontWeight: 500
    },
    reason: {
      color: colors.textColor,
      wordBreak: "break-word"
    },
    placeholder: {
      color: colors.grayText
    },
    loadMore: {
      marginTop: 12,
      textTransform: "none"
    },
    empty: {
      color: colors.grayText,
      padding: "12px 0"
    }
  });

interface Props extends WithStyles<typeof styles> {
  factoringId: string;
}

const formatTimestamp = (iso: string): string => {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    return `${d.toISOString().slice(0, 19).replace("T", " ")} UTC`;
  } catch (e) {
    return iso;
  }
};

const FreezeAuditTrail = ({ classes, factoringId }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const events = useSelector(getFreezeEvents);
  const hasMore = useSelector(getFreezeEventsHasMore);
  const hasFetched = useSelector(getFreezeEventsHasFetched);
  const results: FreezeEvent[] = get(events, "results", []) || [];

  useEffect(() => {
    if (factoringId) {
      dispatch(fetchFreezeEvents(factoringId));
    }
  }, [dispatch, factoringId]);

  const loadMore = (): void => {
    dispatch(fetchFreezeEventsNext(factoringId));
  };

  if (!hasFetched) {
    return (
      <div className={classes.container}>
        <Typography className={classes.empty}>
          Loading audit trail…
        </Typography>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className={classes.container}>
        <Typography className={classes.empty}>
          No freeze history for this client yet.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Table padding="dense">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Date</TableCell>
            <TableCell className={classes.headerCell}>Operation</TableCell>
            <TableCell className={classes.headerCell}>User</TableCell>
            <TableCell className={classes.headerCell}>Reason / Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map(event => {
            const color = EVENT_COLORS[event.event_type] || colors.grayText;
            const actor = event.actor ? event.actor.email : "system";
            const fundingRequestLink =
              event.related_object_type === "FundingRequest" &&
              event.related_object_id
                ? `/admin/fundingrequest/${event.related_object_id}`
                : null;
            const exemptContractType =
              (event.event_type === "EXEMPT_TYPE_ADDED" ||
                event.event_type === "EXEMPT_TYPE_REMOVED") &&
              event.metadata
                ? event.metadata.contract_type
                : null;
            return (
              <TableRow key={event.id}>
                <TableCell className={classes.timestamp}>
                  {formatTimestamp(event.created)}
                </TableCell>
                <TableCell className={classes.operationCell}>
                  <Chip
                    label={event.event_type}
                    size="small"
                    className={classes.chip}
                    style={{ backgroundColor: color }}
                  />
                  {event.operation && (
                    <span className={classes.operationText}>
                      {event.operation}
                    </span>
                  )}
                  {exemptContractType && (
                    <span className={classes.operationText}>
                      {exemptContractType}
                    </span>
                  )}
                  {fundingRequestLink && (
                    <Link to={fundingRequestLink} className={classes.frLink}>
                      FR {event.related_object_id}
                    </Link>
                  )}
                </TableCell>
                <TableCell className={classes.user}>{actor}</TableCell>
                <TableCell>
                  {event.reason ? (
                    <span className={classes.reason}>{event.reason}</span>
                  ) : (
                    <span className={classes.placeholder}>—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {hasMore && (
        <Button
          onClick={loadMore}
          variant="outlined"
          size="small"
          className={classes.loadMore}
        >
          Load more
        </Button>
      )}
    </div>
  );
};

export default withStyles(styles)(FreezeAuditTrail);
